import type { ReactNode } from "react";
import { Collapse } from "@mui/material";
import { MoreInformationSection } from "../sections/MoreInformationSection";
import type { RunRecordBodyProps } from "../../runRecord/RunRecordBody";
import type { DrawerSectionMap } from "./types";

export interface ActiveDrawerBodyProps {
  sections: DrawerSectionMap;
  findingsBlock?: ReactNode | null;
  /** 0-based index of the current step. */
  stepIndex?: number | null;
  /** Total number of steps. */
  totalSteps?: number | null;
  /**
   * The phase / milestone main stream (running-drawer). A ReactNode, not a
   * snapshot: the bodies stay presentation-only and none of them imports
   * `phaseModel.ts`, so the adapter keeps its single-reader rule.
   *
   * Rendered FIRST when present, and it SUPPRESSES the flat steps chip it
   * replaces.
   */
  phaseStream?: ReactNode | null;
  /**
   * The step view (story 26739) — the "what do I do right now" pane for this
   * moment in the run. A ReactNode, not a model: the bodies stay
   * presentation-only, so the host derives the view from `push.status` on
   * every render and hands the finished node down.
   *
   * When present it takes the hero position (ahead of everything else) and,
   * like `phaseStream`, SUPPRESSES the flat steps chip it replaces, the progress
   * percentage and the phase stream.
   * Default `undefined` reproduces today's markup exactly — that is what makes
   * the `emuw-step-views` flag-off path a true rollback rather than a second
   * code path.
   */
  stepView?: ReactNode | null;
  /**
   * The carrier-evidence decision card (running-drawer). Present ONLY when the
   * run carries an `evidence_decision`, never gated on a status literal —
   * gating on the parked status makes the early-override path unreachable
   * (AC-6). Absent prop reproduces today's markup byte for byte.
   */
  decisionCard?: ReactNode | null;
  /** The run record shown behind the more-information entry point. The SAME
   *  record the standalone /workflows/runs/<id> page renders. */
  record: RunRecordBodyProps;
  /** Copy-id / kill-session links — More information pane only. */
  debugActions?: ReactNode | null;
  /** Whether the more-information pane is open. Owned by the top-level
   *  drawer component (survives step/phase updates); the entry-point button
   *  itself lives in the sticky footer, not here. */
  moreInfoOpen?: boolean;
  /** Called when the pane's own back button is clicked. */
  onMoreInfoClose?: () => void;
}

export function ActiveDrawerBody({
  sections,
  findingsBlock,
  stepIndex,
  totalSteps,
  phaseStream,
  stepView,
  decisionCard,
  record,
  debugActions,
  moreInfoOpen = false,
  onMoreInfoClose,
}: ActiveDrawerBodyProps) {
  const pct =
    stepIndex != null && totalSteps
      ? Math.round(((stepIndex + 1) / totalSteps) * 100)
      : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Phases stay above the evidence dropzone so the run progress is
          the first thing in the body; attach sits under it. */}
      {/* The phase stream is the ALWAYS-ON progress display (operator
          directive 2026-09-16): a step view no longer displaces it. Both
          render, stream FIRST, so run progress stays the first thing in the
          body and the step view's reassurance sits under it. */}
      {phaseStream ?? null}
      {stepView ?? null}

      {decisionCard ?? null}

      {/* running-drawer: the submitted request, in the PRIMARY body — on every
          family, including HITL and terminal (AC-8 / AC-9). */}
      {sections.request ?? null}

      {/* Progress bar — only the flag-off body. The phase stream is the
          progress display when the reworked surface is on. */}
      {pct != null && !phaseStream && !stepView && (
        <div className="h-[3px] bg-primary-gray-light-200 rounded-full overflow-hidden mx-0 -mt-1 mb-1">
          <div
            className="h-full bg-sunrise rounded-full transition-all duration-700"
            style={{ width: `${pct}%` }}
          />
        </div>
      )}

      {/* AC-7: ONE entry point over the supporting detail. */}
      <MoreInformationSection
        record={record}
        open={moreInfoOpen}
        onClose={() => onMoreInfoClose?.()}
        extraBlocks={
          <>
            {debugActions ?? null}
          </>
        }
      />

      <Collapse in={!!findingsBlock} unmountOnExit timeout={600}>
        {findingsBlock ?? <span />}
      </Collapse>
    </div>
  );
}

export default ActiveDrawerBody;
